import { motion } from "framer-motion";
import { Sparkles, PenLine, Wand2, Music2 } from "lucide-react";
import PageHeader from "../components/PageHeader";
import CommentsSection from "../components/CommentsSection";
import { aiSongs } from "../data/content";

export default function AIMedia() {
  return (
    <div data-testid="page-ai-media">
      <PageHeader
        eyebrow="AI Media"
        title="Old songs, new tools — with credit where it is due."
        intro="A small collection of songs I have re-imagined with AI. Each video below carries a short note about the song, the original lyricist or source, and the AI tools that helped bring it to life."
        testid="ai-media-header"
      />

      <section className="pb-24">
        <div className="container-narrow">
          <div className="grid lg:grid-cols-2 gap-8" data-testid="ai-media-grid">
            {aiSongs.map((s, i) => (
              <motion.article
                key={`${s.title}-${i}`}
                initial={{ opacity: 0, y: 22 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.55, delay: i * 0.07 }}
                className="rounded-sm bg-[#F2EFEB] border border-[#D8D3CC] overflow-hidden card-lift"
                data-testid={`ai-card-${i}`}
              >
                <div className="aspect-video bg-[#2C2A28]">
                  <iframe
                    title={s.title}
                    src={`https://www.youtube.com/embed/${s.youtubeId}`}
                    className="w-full h-full"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                </div>
                <div className="p-6 sm:p-8">
                  <div className="flex items-center gap-2">
                    <Sparkles size={16} className="text-[#CC7A52]" strokeWidth={1.6} />
                    <span className="eyebrow !text-[#CC7A52] !text-[10px]">
                      AI-assisted
                    </span>
                  </div>
                  <h3 className="font-serif text-2xl sm:text-3xl mt-3 text-[#2C2A28] leading-tight">
                    {s.title}
                  </h3>

                  <div className="mt-5 pb-5 border-b border-dashed border-[#D8D3CC]">
                    <p className="eyebrow !text-[10px]">About the song</p>
                    <p className="mt-2 text-[#2C2A28] leading-relaxed">
                      {s.explanation}
                    </p>
                  </div>

                  <div className="mt-5 flex flex-col gap-4">
                    {s.lyricist && (
                      <div>
                        <div className="flex items-center gap-1.5">
                          <PenLine size={13} className="text-[#4A6B53]" strokeWidth={1.6} />
                          <p className="eyebrow !text-[10px]">Lyrics</p>
                        </div>
                        <p className="mt-1.5 text-sm text-[#5C5852] leading-relaxed">
                          {s.lyricist}
                        </p>
                      </div>
                    )}
                    {s.composition && (
                      <div>
                        <div className="flex items-center gap-1.5">
                          <Music2 size={13} className="text-[#4A6B53]" strokeWidth={1.6} />
                          <p className="eyebrow !text-[10px]">Music composition</p>
                        </div>
                        <p className="mt-1.5 text-sm text-[#5C5852] leading-relaxed">
                          {s.composition}
                        </p>
                      </div>
                    )}
                    {s.aiTool && (
                      <div>
                        <div className="flex items-center gap-1.5">
                          <Wand2 size={13} className="text-[#4A6B53]" strokeWidth={1.6} />
                          <p className="eyebrow !text-[10px]">AI tools used</p>
                        </div>
                        <p className="mt-1.5 text-sm text-[#5C5852] leading-relaxed">
                          {s.aiTool}
                        </p>
                      </div>
                    )}
                  </div>
                  <CommentsSection
                    page="AI Media"
                    itemId={s.title}
                    itemTitle={s.title}
                  />
                </div>
              </motion.article>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
